import axios from 'axios';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

const authHeaders = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
  },
});

const authConfig = () => authHeaders();

export const getOnboardingOverview = async () => {
  const response = await axios.get(
    `${API}/contractor-onboarding/admin-console/overview`,
    authConfig()
  );

  return response.data;
};

export const getDeliveryStatus = async () => {
  const response = await axios.get(
    `${API}/contractor-onboarding/admin/invitation-delivery/status`,
    authConfig()
  );

  return response.data;
};

export const getActivationPreview = async () => {
  const response = await axios.post(
    `${API}/contractor-onboarding/admin/invitation-delivery/activation-preview`,
    {},
    authConfig()
  );

  return response.data;
};

export const getDeactivationPreview = async () => {
  const response = await axios.post(
    `${API}/contractor-onboarding/admin/invitation-delivery/deactivation-preview`,
    {},
    authConfig()
  );

  return response.data;
};

export const activateOperationalDelivery = async (
  reason,
  confirmation
) => {
  const response = await axios.post(
    `${API}/contractor-onboarding/admin/invitation-delivery/activate`,
    {
      reason,
      confirmation,
    },
    authConfig()
  );

  return response.data;
};

export const deactivateOperationalDelivery = async (
  reason,
  confirmation
) => {
  const response = await axios.post(
    `${API}/contractor-onboarding/admin/invitation-delivery/deactivate`,
    {
      reason,
      confirmation,
    },
    authConfig()
  );

  return response.data;
};

export const previewEligibility = async (
  contractorId,
  candidateEmail
) => {
  const response = await axios.post(
    `${API}/contractor-onboarding/admin/invitation-delivery/eligibility-preview`,
    {
      contractor_id: contractorId,
      candidate_email: candidateEmail,
    },
    authConfig()
  );

  return response.data;
};

export const revokeInvitation = async (
  contractorId,
  invitationId,
  reason
) => {
  const response = await axios.post(
    `${API}/contractor-onboarding/contractors/${contractorId}/invitations/${invitationId}/revoke`,
    {
      reason,
    },
    authConfig()
  );

  return response.data;
};

export const resendPreview = async (
  contractorId,
  invitationId
) => {
  const response = await axios.post(
    `${API}/contractor-onboarding/contractors/${contractorId}/invitations/${invitationId}/resend-preview`,
    {},
    authConfig()
  );

  return response.data;
};


// ======================================================================
// V1.0C-H-R10-C ADMIN REVIEW API
// ======================================================================

export const getCandidateReview = async (
  invitationId
) => {
  const response = await axios.get(
    `${API}/contractor-onboarding/admin-console/candidates/${invitationId}/review`,
    authConfig()
  );

  return response.data;
};


export const reviewCandidateEvidence = async (
  contractorId,
  evidenceId,
  reviewStatus,
  reviewNotes = ''
) => {
  const response = await axios.post(
    `${API}/contractor-onboarding/contractors/${contractorId}/evidence/${evidenceId}/review`,
    {
      review_status: reviewStatus,
      review_notes: reviewNotes || null,
    },
    authConfig()
  );

  return response.data;
};


export const openCandidateEvidence = async (
  contractorId,
  evidenceId,
  filename = 'evidence'
) => {
  const response = await axios.get(
    `${API}/contractor-onboarding/contractors/${contractorId}/evidence/${evidenceId}/content`,
    {
      ...authConfig(),
      responseType: 'blob',
    }
  );

  const blob = new Blob(
    [response.data],
    {
      type:
        response.headers?.['content-type']
        || 'application/octet-stream',
    }
  );

  const url = window.URL.createObjectURL(
    blob
  );

  const opened = window.open(
    url,
    '_blank',
    'noopener,noreferrer'
  );

  if (!opened) {
    const link = document.createElement(
      'a'
    );

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  window.setTimeout(
    () => {
      window.URL.revokeObjectURL(
        url
      );
    },
    60000
  );

  return true;
};



// ======================================================================
// V1.0C-H-R10-D-R1 REJECTED EVIDENCE REPLACEMENT
// ======================================================================

export const createEvidenceReplacementRequest = async (
  contractorId,
  evidenceId
) => {
  const response = await axios.post(
    `${API}/contractor-onboarding/contractors/${contractorId}/evidence/${evidenceId}/replacement-request`,
    {},
    authConfig()
  );

  return response.data;
};

// ======================================================================
// V1.0C-H-R10-E-R1 FINAL CANDIDATE APPROVAL
// ======================================================================

export const approveOnboardingCandidate = async (
  invitationId
) => {
  const response = await axios.post(
    `${API}/contractor-onboarding/admin-console/candidates/${invitationId}/approve`,
    {
      confirm: true,
    },
    authConfig()
  );

  return response.data;
};

// ======================================================================
// V1.0C-H-R10-F-R1 AUDIT DASHBOARD
// ======================================================================

export const getOnboardingAuditDashboard = async () => {
  const response = await axios.get(
    `${API}/contractor-onboarding/admin-console/audit-dashboard`,
    authConfig()
  );

  return response.data;
};


export const getOnboardingCandidateAudit = async (
  invitationId
) => {
  const response = await axios.get(
    `${API}/contractor-onboarding/admin-console/audit-dashboard/candidates/${invitationId}`,
    authConfig()
  );

  return response.data;
};

// ======================================================================
// V1.0C-H-R10-J-R2 ADMIN INVITE CANDIDATE
// ======================================================================

export const createCandidateInvitation = async (
  contractorId,
  candidateName,
  candidateEmail,
  ttlHours = 72
) => {
  const response = await axios.post(
    `${API}/contractor-onboarding/contractors/${contractorId}/invitations`,
    {
      candidate_name: candidateName || null,
      candidate_email: candidateEmail,
      ttl_hours: ttlHours,
    },
    authConfig()
  );

  return response.data;
};

// ======================================================================
// RAPID X CONTROLLED PREVIEW-ONLY INVITATION
//
// Creates an invitation ledger record without invoking email delivery.
// Production issue-and-send remains unavailable while delivery is locked.
// ======================================================================

export const createAndSendCandidateInvitation = async (
  contractorId,
  candidateName,
  candidateEmail,
  ttlHours = 72
) => {
  const response = await axios.post(
    `${API}/contractor-onboarding/contractors/${contractorId}/invitations`,
    {
      candidate_name:
        candidateName || null,

      candidate_email:
        candidateEmail,

      ttl_hours:
        ttlHours,
    },
    authConfig()
  );

  return response.data;
};


// RAPID X CONTRACTOR SETUP WIZARD API V1.0A

export const getAvailableSetupContractors = async () => {
  const response = await axios.get(
    `${API}/contractor-onboarding/setup/available-contractors`,
    authConfig()
  );

  return response.data;
};

export const createContractorSetupDraft = async (
  payload
) => {
  const response = await axios.post(
    `${API}/contractor-onboarding/setup/drafts`,
    payload,
    authConfig()
  );

  return response.data;
};

export const updateContractorSetupDraft = async (
  contractorId,
  payload
) => {
  const response = await axios.patch(
    `${API}/contractor-onboarding/setup/drafts/${contractorId}`,
    payload,
    authConfig()
  );

  return response.data;
};

export const getContractorWaiverPreview = async (
  contractorId
) => {
  const response = await axios.get(
    `${API}/contractor-onboarding/setup/drafts/${contractorId}/waiver-preview`,
    authConfig()
  );

  return response.data;
};


// RAPID X GET CONTRACTOR SETUP DRAFT V1.0B

export const getContractorSetupDraft = async (
  contractorId
) => {
  const response = await axios.get(
    `${API}/contractor-onboarding/setup/drafts/${contractorId}`,
    authConfig()
  );

  return response.data;
};

